import CodeMirror from 'codemirror';
import { IGradeHierarchy, IPointAccount, IPromptMessages, ICustomProperty } from '../index.d';
import { getCurrentMemberGradeId } from '../hints/trigger/current-member-grade';
import { createSpanReplacementNode, createWarningSpanReplacementNode } from '../markers/utils';
import { getCloseBracketPosition, splitTokensWithComma, hasTokenIncludes } from '../utils/codemirror';


function isIdToken(token: CodeMirror.Token) {
    return hasTokenIncludes(token, ['declaration', 'Id']);
}


export function replaceMemberGradeHierarchyGradeId(
    editor: CodeMirror.Editor,
    tokens: CodeMirror.Token[],
    idx: number,
    gradeHierarchies: IGradeHierarchy[],
    customProperties: ICustomProperty[]
) {
    const token = tokens[idx];
    if (!token) { return 0; }


    const aduit: any = { mode: '', count: 0, endPosCh: 0, tokens: [] };

    // 是否为完整的Id声明类型（单个ID）
    const isCompleteId = isIdToken(token);
    if (isCompleteId) {
        aduit.mode = 'Id';
        aduit.count = 1;
        aduit.endPosCh = token.end;
        aduit.tokens = [token];
    } else if (token.type === 'bracket' && token.string === '[') {
        // Id数组
        const closeBracketIndex = getCloseBracketPosition(tokens, idx, '[', ']');

        if (closeBracketIndex > -1) {
            const tokensArr = splitTokensWithComma(tokens.slice(idx + 1, closeBracketIndex));
            if (tokensArr.every(tokens => isIdToken(tokens[0]))) {
                aduit.mode = 'IdArray';
                aduit.count = closeBracketIndex - idx + 1;
                aduit.endPosCh = tokens[closeBracketIndex].end;
                aduit.tokens = tokensArr;
            }
        }
    }

    if (!aduit.mode) { return 0; }

    // 判断当前Token为Id声明，且前面为`CURRENT_MEMBER_GRADE(123)`函数调用
    const position = { line: token.state.line, ch: token.end };
    const result = getCurrentMemberGradeId(editor, position, gradeHierarchies.map(item => item.id), customProperties);

    const gradeHierarchyId = result ? result.id : undefined;
    const gradeHierarchy = gradeHierarchies.find(item => item.id === gradeHierarchyId);

    if (!gradeHierarchy) { return 0; }

    const doc = editor.getDoc();
    const line = token.state.line;
    const startPos = { line, ch: token.start };
    const endPos = { line, ch: aduit.endPosCh };

    if (aduit.mode === 'Id') {
        const gradeId = Number(aduit.tokens[0].string.match(/^Id\((\d+)\)/)[1]);
        const grade = gradeHierarchy.grades.find(item => item.id === gradeId);
        if (grade) {
            const node = createSpanReplacementNode(grade.name, 'cm-grade-hierarchy-grade-id');
            doc.markText(startPos, endPos, {
                replacedWith: node
            });
            return aduit.count;
        }
    } else {
        const gradeNames = aduit.tokens.map((tokenArr: CodeMirror.Token[]) => {
            const gradeId = Number(tokenArr[0].string.match(/^Id\((\d+)\)/)![1]);
            const grade = gradeHierarchy.grades.find(item => item.id === gradeId)!;
            return grade ? grade.name : 'Unknown';
        }).join(',');
        const node = createSpanReplacementNode(`[${gradeNames}]`, 'cm-grade-hierarchy-grade-id');
        doc.markText(startPos, endPos, {
            replacedWith: node
        });
        return aduit.count;
    }

    return 0;
}


export function replaceMemberPointAccount(
    editor: CodeMirror.Editor,
    token: CodeMirror.Token,
    pointAccounts: IPointAccount[],
    promptMessages: IPromptMessages
) {
    if (!token || !token.type) {
        return 0;
    }
    const types = token.type.split(' ');
    if (types.includes('CURRENT_MEMBER_POINT')) {
        const line = token.state.line;
        const startPos = { line, ch: token.start };
        const endPos = { line, ch: token.end };

        const id = token.string.match(/^CURRENT_MEMBER_POINT\((\d+)\)/i)![1];

        const pointAccount = pointAccounts.find(item => item.id === Number(id));

        let replacedWith;
        if (!pointAccount) {
            replacedWith = createWarningSpanReplacementNode(
                promptMessages.invalidPointAccount || 'Invalid Point Account',
                'cm-point-account',
                promptMessages.invalidPointAccountTooltip
            );
        } else if (pointAccount.disabled) {
            replacedWith = createWarningSpanReplacementNode(
                pointAccount.name,
                'cm-point-account',
                promptMessages.disabledPointAccountTooltip
            );
        } else {
            replacedWith = createSpanReplacementNode(
                pointAccount.name,
                'cm-point-account'
            );
        }
        editor.getDoc().markText(startPos, endPos, {
            replacedWith
        });
        return 1;
    }
    return 0;
}


export function replaceMemberGradeHierarchy(
    editor: CodeMirror.Editor,
    token: CodeMirror.Token,
    gradeHierarchies: IGradeHierarchy[],
    promptMessages: IPromptMessages
) {
    if (!token || !token.type) {
        return 0;
    }
    const types = token.type.split(' ');
    if (types.includes('CURRENT_MEMBER_GRADE')) {
        const line = token.state.line;
        const startPos = { line, ch: token.start };
        const endPos = { line, ch: token.end };

        const id = token.string.match(/^CURRENT_MEMBER_GRADE\((\d+)\)/i)![1];

        const gradeHierarchy = gradeHierarchies.find(item => item.id === Number(id));

        let replacedWith;
        if (!gradeHierarchy) {
            replacedWith = createWarningSpanReplacementNode(
                promptMessages.invalidGradeHierarchy || 'Invalid Grade Hierarchy',
                'cm-grade-hierarchy',
                promptMessages.invalidGradeHierarchyTooltip
            );
        } else if (gradeHierarchy.disabled) {
            replacedWith = createWarningSpanReplacementNode(
                gradeHierarchy.name,
                'cm-grade-hierarchy',
                promptMessages.disabledGradeHierarchyTooltip
            );
        } else {
            replacedWith = createSpanReplacementNode(
                gradeHierarchy.name,
                'cm-grade-hierarchy'
            );
        }
        editor.getDoc().markText(startPos, endPos, {
            replacedWith
        });
        return 1;
    }
    return 0;
}
